import type { EventBus } from "../core/EventBus.ts";
import type { Card, CardDataFile, CardEffect } from "../entities/types.ts";
import type { Rng } from "../core/Rng.ts";
import type { PayloadRack } from "./PayloadRack.ts";

/** Soft cap: above threshold, each additional × gives diminishing returns.
 *  At threshold=3: 3× passes through, 6× → ~4.2×, 12× → ~4.8× (hard cap ~5×).
 */
function softCapMultiplier(raw: number, threshold = 3, hardCap = 5): number {
  if (raw <= threshold) return raw;
  const excess = raw - threshold;
  const range = hardCap - threshold;
  return threshold + range * (1 - Math.exp(-excess / range));
}

export const MAX_SLOTS = 5;
export const SKIP_REWARD = 50;

/** Rarity multipliers per epoch for wave-based pool shifting. */
export const RARITY_EPOCH_I: Record<string, number> = {
  // Playtest-tunable early-epoch nudge: more epic visibility, rare legendary chance.
  common: 2.5,
  rare: 1.0,
  epic: 0.7,
  legendary: 0.25,
};
export const RARITY_EPOCH_II: Record<string, number> = {
  common: 1.5,
  rare: 2.0,
  epic: 1.0,
  legendary: 0.5,
};
export const RARITY_EPOCH_III: Record<string, number> = {
  common: 1.0,
  rare: 2.0,
  epic: 2.0,
  legendary: 1.5,
};

export class DraftSystem {
  private cardPool: Card[];
  private originalPool: Card[];
  private activeCards: Card[] = [];
  private bus: EventBus;
  private currentWave = 0;
  private maxSlotCount: number;
  private skipRewardAmount: number;
  private drawCount_ = 3;
  private rng: () => number;
  private payloadRack_: PayloadRack | null = null;
  /** D-322 / AC-5 (Task-007) — the resolved per-epoch rarity tables, data-driven
   *  via `cards.json` `rarityEpochs`. Default = byte-identical pre-D-322
   *  module constants. Mirrors the `maxSlots ?? MAX_SLOTS` seam above. */
  private readonly rarityEpochs: {
    I: Record<string, number>;
    II: Record<string, number>;
    III: Record<string, number>;
  };

  constructor(cardsData: CardDataFile, bus: EventBus, rng: Rng) {
    this.originalPool = [...cardsData.cards];
    this.cardPool = [...this.originalPool];
    this.maxSlotCount = cardsData.maxSlots ?? MAX_SLOTS;
    this.skipRewardAmount = cardsData.skipReward ?? SKIP_REWARD;
    this.rarityEpochs = {
      I: cardsData.rarityEpochs?.I ?? RARITY_EPOCH_I,
      II: cardsData.rarityEpochs?.II ?? RARITY_EPOCH_II,
      III: cardsData.rarityEpochs?.III ?? RARITY_EPOCH_III,
    };
    this.bus = bus;
    this.rng = () => rng.next();
  }

  /** Wire the payload rack for draw-pool exclusion. */
  setPayloadRack(rack: PayloadRack): void {
    this.payloadRack_ = rack;
  }

  get cards(): readonly Card[] {
    return this.activeCards;
  }

  get maxSlots(): number {
    return this.maxSlotCount;
  }

  set maxSlotOverride(val: number) {
    this.maxSlotCount = val;
  }

  set drawCount(val: number) {
    this.drawCount_ = val;
  }

  get drawCount(): number {
    return this.drawCount_;
  }

  get skipReward(): number {
    return this.skipRewardAmount;
  }

  get isFull(): boolean {
    return this.activeCards.length >= this.maxSlotCount;
  }

  get slotCount(): number {
    return this.activeCards.length;
  }

  drawCards(wave: number, count = 3): Card[] {
    this.currentWave = wave;
    const eligible = this.getEligibleCards().filter(
      (c) => c.weight * this.getRarityMultiplier(c.rarity) > 0,
    );
    const drawn: Card[] = [];

    for (let i = 0; i < count && eligible.length > 0; i++) {
      const card = this.weightedDraw(eligible);
      if (!card) break;
      drawn.push(card);
      const idx = eligible.indexOf(card);
      if (idx !== -1) eligible.splice(idx, 1);
    }

    // GDD rule: at least 1 card from a different category
    if (drawn.length >= 3) {
      const categories = drawn.map((c) => c.category);
      const allSame = categories.every((c) => c === categories[0]);
      if (allSame) {
        const other = eligible.find((c) => c.category !== categories[0]);
        if (other) drawn[drawn.length - 1] = other;
      }
    }

    if (drawn.length > 0) {
      this.bus.emit("draft.cardsDrawn", { cards: drawn, wave });
    }

    return drawn;
  }

  selectCard(card: Card): boolean {
    // Instant cards fire immediately and never occupy a slot
    if (card.effect.target === "instant") {
      this.bus.emit("draft.cardSelected", { card });
      return true;
    }

    if (this.isFull) return false;
    if (this.activeCards.some((c) => c.id === card.id)) return false;

    if (card.duration) {
      card._selectedAtWave = this.currentWave;
    }
    this.activeCards.push(card);
    this.bus.emit("draft.cardSelected", { card });
    return true;
  }

  swapCard(slotIndex: number, newCard: Card): Card | null {
    if (slotIndex < 0 || slotIndex >= this.activeCards.length) return null;

    const unloaded = this.activeCards[slotIndex];
    if (!unloaded) return null;
    this.returnToPool(unloaded);
    this.bus.emit("draft.cardUnloaded", { card: unloaded, slotIndex });

    if (newCard.duration) {
      newCard._selectedAtWave = this.currentWave;
    }
    // Preserve slot order: new card replaces unloaded at the same index
    // (previously the splice+push pattern moved the new card to the end).
    this.activeCards.splice(slotIndex, 1, newCard);
    this.bus.emit("draft.cardSelected", { card: newCard });
    return unloaded;
  }

  expireCards(currentWave: number): Card[] {
    const expired: Card[] = [];
    this.activeCards = this.activeCards.filter((card) => {
      if (
        card.duration &&
        card._selectedAtWave !== undefined &&
        currentWave > card._selectedAtWave + card.duration
      ) {
        expired.push(card);
        return false;
      }
      return true;
    });
    for (const card of expired) {
      this.returnToPool(card);
      this.bus.emit("draft.cardExpired", { card });
    }
    return expired;
  }

  getActiveModifiers(target: string): CardEffect[] {
    const modifiers: CardEffect[] = [];
    for (const card of this.activeCards) {
      if (card.effect.target === target) {
        modifiers.push(card.effect);
      }
    }
    return modifiers;
  }

  getDamageMultiplier(element: string): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("combat")) {
      if (mod.type === "elementDamageMultiplier" && mod.element === element) {
        multiplier *= mod.value as number;
      }
    }
    return softCapMultiplier(multiplier);
  }

  getKillGoldMultiplier(): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("economy")) {
      if (mod.type === "killGoldMultiplier") {
        multiplier *= mod.value as number;
      }
    }
    return softCapMultiplier(multiplier);
  }

  getGlobalDamageMultiplier(): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("combat")) {
      if (mod.type === "globalDamageMultiplier") {
        multiplier *= mod.value as number;
      }
    }
    return softCapMultiplier(multiplier);
  }

  getGlobalFireRateMultiplier(): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("combat")) {
      if (mod.type === "globalFireRateMultiplier") {
        multiplier *= mod.value as number;
      }
    }
    return softCapMultiplier(multiplier, 3, 5);
  }

  hasGlobalSlow(): number {
    let slow = 1;
    for (const mod of this.getActiveModifiers("enemies")) {
      if (mod.type === "globalSlow") slow *= mod.value as number;
    }
    return slow;
  }

  hasDiagonalSynergy(): boolean {
    for (const mod of this.getActiveModifiers("synergy")) {
      if (mod.type === "diagonalSynergy") return true;
    }
    return false;
  }

  /** MINIMALIST bonus: +value per empty slot. */
  getMinimalistBonus(): number {
    let bonus = 0;
    for (const mod of this.getActiveModifiers("combat")) {
      if (mod.type === "minimalist") {
        const emptySlots = this.maxSlotCount - this.activeCards.length;
        bonus += (mod.value as number) * emptySlots;
      }
    }
    return 1 + bonus;
  }

  /** RESONANCE bonus: +value per active synergy (needs synergy count injected). */
  getResonanceBonus(activeSynergyCount: number): number {
    let bonus = 0;
    for (const mod of this.getActiveModifiers("synergy")) {
      if (mod.type === "resonance") {
        bonus += (mod.value as number) * activeSynergyCount;
      }
    }
    return 1 + bonus;
  }

  /** GREED_PROTOCOL: kill-gold bonus from tradeoff cards. */
  getGreedGoldMultiplier(): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("tradeoff")) {
      if (mod.type === "greedProtocol") {
        multiplier *= mod.value as number;
      }
    }
    return multiplier;
  }

  /** GREED_PROTOCOL + SCORCHED_EARTH: damage penalty from tradeoff cards. */
  getTradeoffDamagePenalty(): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("tradeoff")) {
      if (mod.type === "greedProtocol") {
        multiplier *= mod.penalty ?? 0.8;
      }
    }
    return multiplier;
  }

  /** SCORCHED_EARTH: gold penalty. */
  getScorchedGoldPenalty(): number {
    let multiplier = 1;
    for (const mod of this.getActiveModifiers("tradeoff")) {
      if (mod.type === "scorchedEarth") {
        multiplier *= mod.penalty ?? 0.5;
      }
    }
    return multiplier;
  }

  /** APEX_PREDATOR: returns {boost, penalty} or null if not active. */
  getApexPredatorEffect(): { boost: number; penalty: number } | null {
    for (const card of this.activeCards) {
      if (card.effect.type === "apexPredator") {
        return {
          boost: card.effect.value as number,
          penalty: card.effect.penalty ?? 0.6,
        };
      }
    }
    return null;
  }

  /** DEAD_MAN'S_SWITCH: returns damage multiplier based on current lives. */
  getDeadMansSwitchMultiplier(currentLives: number): number {
    for (const card of this.activeCards) {
      if (card.effect.type === "deadMansSwitch") {
        const threshold = card.effect.threshold ?? 3;
        if (currentLives < threshold) {
          return card.effect.value as number;
        }
      }
    }
    return 1;
  }

  /** Whether MONOCULTURE is active (synergy system reads this). */
  hasMonoculture(): boolean {
    for (const card of this.activeCards) {
      if (card.effect.type === "monoculture") return true;
    }
    return false;
  }

  reset(): void {
    this.activeCards = [];
    this.currentWave = 0;
    this.cardPool = [...this.originalPool];
  }

  private returnToPool(card: Card): void {
    const original = this.originalPool.find((c) => c.id === card.id);
    if (original && !this.cardPool.includes(original)) {
      this.cardPool.push(original);
    }
  }

  private getEligibleCards(): Card[] {
    const activeIds = new Set(this.activeCards.map((c) => c.id));
    const armedIds = this.payloadRack_?.armedIds() ?? new Set<string>();
    return this.cardPool.filter((card) => {
      if (card.minWave > this.currentWave) return false;
      if (activeIds.has(card.id)) return false;
      if (armedIds.has(card.id)) return false;
      return true;
    });
  }

  private weightedDraw(eligible: Card[]): Card | null {
    if (eligible.length === 0) return null;
    let totalWeight = 0;
    for (const card of eligible) {
      totalWeight += card.weight * this.getRarityMultiplier(card.rarity);
    }

    let roll = this.rng() * totalWeight;
    for (const card of eligible) {
      roll -= card.weight * this.getRarityMultiplier(card.rarity);
      if (roll <= 0) return card;
    }
    return eligible[eligible.length - 1] ?? null;
  }

  /** Wave-epoch rarity multiplier for pool shifting. */
  private getRarityMultiplier(rarity: string): number {
    const wave = this.currentWave;
    if (wave <= 12) {
      return this.rarityEpochs.I[rarity] ?? 1;
    } else if (wave <= 24) {
      return this.rarityEpochs.II[rarity] ?? 1;
    }
    return this.rarityEpochs.III[rarity] ?? 1;
  }
}
